/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { TestIds } from '../testIds';
import { PageContainer } from '../components/layout/PageContainer';
import TitleBox from '../components/TitleBox';
import Tabs, { TabPanel } from '../components/Tabs';
import ActiveAgreementsTabPanel from '../features/sales-agreements/ActiveAgreements';
import PastAgreementsTabPanel from '../features/sales-agreements/PastAgreements';
import { useSelector } from 'react-redux';
import {
  getSalesAgreements,
  selectAllSalesAgreements,
  selectAgreementsRequestStatus,
  selectPastSalesAgreements,
  selectActiveSalesAgreements,
  selectUpcomingSalesAgreements,
} from '../features/sales-agreements/storage';
import { useAppDispatch } from '../store/rootStore';
import { selectCurrentFarmerAccount } from '../features/azure-b2c/storage';
import { Loader, LoadingBox } from '../components/Loader';
import NoSalesAgreement from '../features/sales-agreements/NoSalesAgreement';
import { CONTACT_CARD_TITLE, CONTENT_LAYOUT_WIDTH } from '../util/consts';
import Section from '../components/layout/Section';
import { Box, Theme, useMediaQuery } from '@mui/material';
import { Declarations } from '../features/declarations/components/DeclarationsButton';
import { useScrollRestoration } from '../util/useScrollRestoration';
import { useLocation, useNavigate } from 'react-router-dom';
import CampaignModal from '../features/sales-agreements/components/CampaignModal/CampaignModal';
import { AgreementsCampaignsAtomic } from '../services/atomic/AgreementsCampaignsAtomic';

export function SalesAgreements() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const requestStatus = useSelector(selectAgreementsRequestStatus);
  const selectedAccount = useSelector(selectCurrentFarmerAccount);
  const salesAgreements = useSelector(selectAllSalesAgreements);
  const pastAgreements = useSelector(selectPastSalesAgreements);
  const activeAgreements = useSelector(selectActiveSalesAgreements);
  const upcomingAgreements = useSelector(selectUpcomingSalesAgreements);
  const ref = useRef<HTMLElement>(null);
  useScrollRestoration({
    ref,
    scrollYPositionKey: sessionStorage.scrollYPositionOnAgreements,
    path: '/agreement/',
  });

  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const pageType = parseInt(query.get('tab') ?? '0', 10);
  const [tab, setTab] = React.useState(pageType);

  useEffect(() => {
    query.set('tab', tab.toString());
    navigate({
      pathname: '/agreements',
      search: query.toString(),
    });
  }, [tab]);

  const pastAvailableOnly = useMemo(() => {
    return (
      pastAgreements?.length !== 0 &&
      activeAgreements?.length === 0 &&
      upcomingAgreements?.length === 0
    );
  }, [pastAgreements, activeAgreements, upcomingAgreements]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatchGetSalesAgreements();
  }, [selectedAccount]);

  const dispatchGetSalesAgreements = () => {
    if (selectedAccount) {
      dispatch(getSalesAgreements(selectedAccount.accountNumber));
    }
  };

  const handleCampaignModalClosed = () => {
    query.delete('campaignId');
    navigate({
      pathname: '/agreements',
      search: query.toString(),
    });
  };

  const tabsInfo = [
    {
      label: 'Active agreements',
      'data-testid': TestIds.feat.agreement.tabView.activeAgreements,
      details: (
        <ActiveAgreementsTabPanel
          activeAgreements={activeAgreements}
          upcomingAgreements={upcomingAgreements}
        />
      ),
      default: !pastAvailableOnly,
    },
    {
      label: 'Closed agreements',
      'data-testid': TestIds.feat.agreement.tabView.pastAgreements,
      details: <PastAgreementsTabPanel pastAgreements={pastAgreements} />,
      default: pastAvailableOnly,
    },
  ];

  const tabPanels = React.useMemo(() => {
    return (
      <>
        <Section width={'100%'}>
          {isMd && (
            <Box position='absolute' right='0px'>
              <Declarations />
            </Box>
          )}
          <TitleBox display='Sales Agreements' />

          <AgreementsCampaignsAtomic />

          <Tabs
            value={tab}
            setValue={setTab}
            tabsInfo={tabsInfo}
            aria-label='sales agreement tabs'
            id='sales-agreement'
          />
        </Section>
        {tabsInfo.map((x, index) => (
          <TabPanel key={`${x.label}-${tab}`} value={tab} index={index} id={'sales-agreement'}>
            {x.details}
          </TabPanel>
        ))}
      </>
    );
  }, [salesAgreements, tab, isMd]);

  const memo = React.useMemo(() => {
    if (salesAgreements.length === 0 && requestStatus === 'pending') {
      return (
        <>
          <TitleBox display='Sales Agreements' />
          <LoadingBox>
            <Loader title='Loading sales agreements' />
          </LoadingBox>
        </>
      );
    } else if (salesAgreements && Object.keys(salesAgreements).length > 0) {
      return <>{tabPanels}</>;
    } else
      return (
        <>
          <Section width={'100%'} mb={2}>
            {isMd && (
              <Box position='absolute' right='0px'>
                <Declarations />
              </Box>
            )}
            <TitleBox display='Sales Agreements' />

            <AgreementsCampaignsAtomic />
            <Tabs
              value={tab}
              setValue={setTab}
              tabsInfo={tabsInfo}
              aria-label='sales agreement tabs'
              id='sales-agreement'
            />
          </Section>

          <NoSalesAgreement
            display=''
            body='You do not have any Sales Agreements associated with this account. If you would like to know more about Sales Agreements at Farmlands or set up a contract for product please contact your Farmlands sales representative. '
            contactTitle={CONTACT_CARD_TITLE.noAgreements}
          />
        </>
      );
  }, [requestStatus, selectedAccount, salesAgreements, tabPanels]);

  return (
    <PageContainer
      data-testid={TestIds.pages.salesAgreements}
      sx={{ maxWidth: `${CONTENT_LAYOUT_WIDTH}px` }}
    >
      {memo}
      <CampaignModal
        campaignId={query.get('campaignId')}
        onModalClosed={handleCampaignModalClosed}
      />
    </PageContainer>
  );
}

export default SalesAgreements;
